import React, { useState, useEffect } from "react";
import { Settings2, RefreshCcwIcon } from "lucide-react";
import { toast } from "sonner";
import {
  getConnectorInfo
} from "../../../../service/apiService";

// Define Connector type
interface Connector {
  id: string;
  name: string;
  type: string;
  api_key: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

interface ConnectorSelectorProps {
  isEnabled: boolean;
  selectedConnectorIds: string[];
  onConnectorSelected: (connectorId: string) => void;
  onConnectorRemoved: (connectorId: string) => void;
}

const ConnectorSelector: React.FC<ConnectorSelectorProps> = ({
  isEnabled,
  selectedConnectorIds,
  onConnectorSelected,
  onConnectorRemoved
}) => {
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch connectors when enabled
  useEffect(() => {
    if (isEnabled) {
      fetchConnectors();
    }
  }, [isEnabled]);

  const fetchConnectors = async () => {
    setLoading(true);
    try {
      const response = await getConnectorInfo();
      setConnectors(response.connectors || []);
    } catch (error) {
      console.error("Error fetching connectors:", error);
      toast.error("Failed to load connectors");
    } finally {
      setLoading(false);
    }
  };

  // Handle connector selection 
  const handleConnectorToggle = (connectorId: string, isActive: boolean) => {
    // Prevent selection if connector is inactive
    if (!isActive) {
      toast.error("Cannot select inactive connector");
      return;
    }

    const isSelected = selectedConnectorIds.includes(connectorId);
    
    if (isSelected) {
      onConnectorRemoved(connectorId);
    } else {
      onConnectorSelected(connectorId);
    }
  };

  // Render different states
  if (!isEnabled) {
    return (
      <div className="text-sm text-gray-500 text-center py-4">
        Enable Kyva Connectors to select connectors
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <RefreshCcwIcon className="animate-spin h-5 w-5 text-gray-400" />
      </div>
    );
  }

  if (connectors.length === 0) {
    return (
      <div className="text-sm text-gray-500 text-center py-4">
        No connectors available
      </div>
    );
  }

  // Filter and separate active and inactive connectors
  const activeConnectors = connectors.filter(connector => connector.status === 'active');
  const inactiveConnectors = connectors.filter(connector => connector.status !== 'active');

  return (
    <div className="space-y-1">
      {/* Active Connectors */}
      {activeConnectors.map(connector => {
        const isSelected = selectedConnectorIds.includes(connector.id);
        
        return (
          <div 
            key={connector.id}
            className={`flex items-center justify-between p-1.5 rounded border cursor-pointer hover:bg-gray-50 ${
              isSelected ? 'bg-violet-50 border-violet-200' : 'bg-gray-50 border-gray-200'
            }`}
            onClick={() => handleConnectorToggle(connector.id, true)}
          >
            <div className="flex items-center space-x-1.5 flex-1 min-w-0">
              <Settings2 className="h-3 w-3 text-gray-600 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-gray-900 truncate">
                  {connector.name}
                </div>
                <div className="text-xs text-gray-500 capitalize">
                  {connector.type}
                </div>
              </div>
            </div>
            <div className="flex items-center ml-1">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleConnectorToggle(connector.id, true)}
                className="h-3 w-3 text-violet-600 focus:ring-violet-500 border-gray-300 rounded cursor-pointer"
                onClick={(e) => e.stopPropagation()} // Prevent double toggle
              />
            </div>
          </div>
        );
      })}

      {/* Inactive Connectors - Disabled */}
      {inactiveConnectors.map(connector => (
        <div 
          key={connector.id}
          className="flex items-center justify-between p-1.5 rounded border bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed"
          title="This connector is inactive and cannot be selected"
        >
          <div className="flex items-center space-x-1.5 flex-1 min-w-0">
            <Settings2 className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-gray-500 truncate">
                {connector.name}
              </div>
              <div className="text-xs text-gray-400 capitalize">
                {connector.type} (Inactive)
              </div>
            </div>
          </div>
          <div className="flex items-center ml-1">
            <input
              type="checkbox"
              checked={false}
              disabled={true}
              className="h-3 w-3 text-gray-300 border-gray-300 rounded cursor-not-allowed"
            />
          </div>
        </div>
      ))}

      {/* Show message if no active connectors */}
      {activeConnectors.length === 0 && inactiveConnectors.length > 0 && (
        <div className="text-sm text-amber-600 text-center py-2 bg-amber-50 rounded border border-amber-200">
          All connectors are inactive. Please activate them in Settings.
        </div>
      )}
    </div>
  );
};

export default ConnectorSelector;